import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Users, Zap } from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { useSand, type SandPool } from '../hooks/useSand';
import { SAND_PRMD_MASTER, SAND_GRAM_ICON } from '../ton/sand';
import { formatTon } from '../lib/format';
import { LiveFeed } from './LiveFeed';
import { PrmdAccrual } from './PrmdAccrual';
import {
  CoinFlipArt,
  DiceArt,
  PlinkoArt,
  RocketArt,
  WheelArt,
} from './GameArt';

interface LobbyProps {
  state: PyramidaUiState;
  address: string | null;
  onGoLottery: () => void;
  onGoSand: () => void;
  onGoCoinFlip: () => void;
  onGoRocket: () => void;
  onGoPlinko: () => void;
  onGoWheel: () => void;
}

const fmtPool = (raw: bigint, decimals: number) => {
  const v = Number(raw) / 10 ** decimals;
  return v >= 1000
    ? Math.round(v).toLocaleString('en-US').replace(/,/g, ' ')
    : v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/** Instant games live as standalone pages while they are being polished one by
 *  one; the lobby links to them. Adding a game = adding an entry here. */
const GAMES: {
  id: string;
  name: string;
  mult: string;
  /** External page; internal games (no url) open inside the app shell. */
  url?: string;
  Art: () => JSX.Element;
  hot?: boolean;
}[] = [
  {
    id: 'coinflip',
    name: 'CoinFlip',
    mult: 'x1.76',
    Art: CoinFlipArt,
    hot: true,
  },
  {
    id: 'rocket',
    name: 'Rocket',
    mult: 'x1.01–x17.60',
    Art: RocketArt,
    hot: true,
  },
  {
    id: 'plinko',
    name: 'Plinko',
    mult: 'x0.40–x12.00',
    Art: PlinkoArt,
    hot: true,
  },
  {
    id: 'wheel',
    name: 'Wheel',
    mult: 'x0.40–x4.80',
    Art: WheelArt,
    hot: true,
  },
];

function useNow(): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** Animates a number towards its target with an ease-out ramp — the prize bank
 *  "counts up" instead of snapping. */
function useCountUp(target: number, durationMs = 900): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(0);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;
    const start = performance.now();
    const tick = (ts: number) => {
      const p = Math.min(1, (ts - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = from + (target - from) * eased;
      setValue(v);
      fromRef.current = v;
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs]);
  return value;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

const cardStagger = {
  hidden: { opacity: 0, y: 16 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 * i, duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

export function Lobby({
  state,
  address,
  onGoLottery,
  onGoSand,
  onGoCoinFlip,
  onGoRocket,
  onGoPlinko,
  onGoWheel,
}: LobbyProps) {
  const { t } = useTranslation();
  const now = useNow();
  const { pools, roundSeconds } = useSand(address);
  // Same ordering as the Sand screen: ripe pools → running rounds (richest
  // first) → empty tables by wallet USD support; PRMD leads, GRAM follows.
  const sandTables = (() => {
    const rank = (p: SandPool) =>
      p.master === SAND_PRMD_MASTER ? 0 : p.key === 'gram' ? 1 : 2;
    const hot = pools
      .filter((p) => p.live && p.endTime <= now)
      .sort((a, b) => rank(a) - rank(b) || b.poolUsd - a.poolUsd || a.endTime - b.endTime);
    const live = pools
      .filter((p) => p.live && p.endTime > now)
      .sort((a, b) => rank(a) - rank(b) || b.poolUsd - a.poolUsd || a.endTime - b.endTime);
    const idle = pools
      .filter((p) => !p.live)
      .sort(
        (a, b) => rank(a) - rank(b) || b.usd - a.usd || a.symbol.localeCompare(b.symbol),
      );
    return [...hot, ...live, ...idle];
  })();
  const firstLoad = state.lastUpdated === null;
  const prize = useCountUp(state.lottery.projectedPrize);

  const left = Math.max(0, state.lottery.roundEndTime - now);
  // Ripe round: time is up but the lazy draw hasn't been triggered yet — the
  // next ticket purchase runs the draw and opens a new round.
  const ripe = !firstLoad && state.lottery.roundEndTime > 0 && left === 0;
  const d = Math.floor(left / 86400);
  const h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;

  // Round progress: how much of the 7-day round has elapsed.
  const ROUND_S = 7 * 86400;
  const progress = Math.min(1, Math.max(0, (ROUND_S - left) / ROUND_S));

  const lastWin = {
    address: state.lottery.lastWinnerAddress,
    amount: state.lottery.lastWinAmount,
    utime: state.lottery.lastLotteryTime,
  };

  return (
    <section className="content-section lobby">
      <LiveFeed lastWin={lastWin} />

      {/* flagship: the GRAM table as one big coin, Sand-table style */}
      <motion.button
        type="button"
        className="gtable"
        onClick={onGoLottery}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="gtable__row">
          <span className="gtable__wrap">
            <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
              {ripe ? (
                <circle className="gtable__rhot" cx="48" cy="48" r="44" strokeWidth="4" />
              ) : (
                <>
                  <circle className="gtable__rbase" cx="48" cy="48" r="44" strokeWidth="4" />
                  <circle
                    className="gtable__rarc"
                    cx="48"
                    cy="48"
                    r="44"
                    strokeWidth="4"
                    strokeDasharray={2 * Math.PI * 44}
                    strokeDashoffset={2 * Math.PI * 44 * progress}
                  />
                </>
              )}
            </svg>
            <span className="gtable__coin">
              <img src={SAND_GRAM_ICON} alt="" draggable={false} />
            </span>
          </span>
          <span className="gtable__info">
            <span className="gtable__tag">{t('v2.lobby.gramTable')}</span>
            <span className="gtable__prize">
              {firstLoad ? (
                <span className="skeleton skeleton--hero" />
              ) : (
                <>
                  {formatTon(prize)} <b>GRAM</b>
                </>
              )}
            </span>
            {ripe ? (
              <span className="gtable__timer gtable__timer--ripe">
                {t('v2.lobby.drawReady')}
              </span>
            ) : (
              <span className="gtable__timer" role="timer">
                {d > 0 ? `${d}${t('v2.lobby.days').slice(0, 1)} ` : ''}
                {pad(h)}:{pad(m)}:{pad(s)}
              </span>
            )}
            <span className="gtable__meta">
              <span className="gtable__chip">
                <Users size={12} aria-hidden="true" /> {state.lottery.participantsCount}
              </span>
              {state.me && state.me.roundStakeKnown && state.me.roundStakeTon > 0 ? (
                <span className="gtable__chip">
                  {t('v2.lobby.myTickets', { amount: formatTon(state.me.roundStakeTon) })}
                </span>
              ) : (
                <span className="gtable__chip">
                  {t('v2.lobby.minEntry', { price: formatTon(state.lottery.ticketPrice) })}
                </span>
              )}
            </span>
          </span>
        </span>
        <span className="gtable__cta">
          <span className="hero2__shimmer" aria-hidden="true" />
          {t('v2.lobby.lotteryCta')}
        </span>
        <PrmdAccrual token={state.token} variant="lobby" />
      </motion.button>

      {/* sand tables: one scrollable row of coins, Sand-screen order */}
      <div className="lobby__coins">
        {sandTables.map((p) => {
          const tl = p.live ? p.endTime - now : -1;
          const isHot = p.live && tl <= 0;
          const R = 26;
          const C = 2 * Math.PI * R;
          const frac = p.live && !isHot ? Math.max(0, Math.min(1, tl / roundSeconds)) : 0;
          return (
            <button
              key={p.key}
              type="button"
              className={`scoin scoin--${isHot ? 'hot' : p.live ? 'live' : 'idle'}`}
              onClick={onGoSand}
            >
              <span className="scoin__wrap">
                <svg width="58" height="58" viewBox="0 0 58 58" aria-hidden="true">
                  {isHot ? (
                    <circle className="scoin__rhot" cx="29" cy="29" r={R} strokeWidth="3" />
                  ) : (
                    <>
                      <circle className="scoin__rbase" cx="29" cy="29" r={R} strokeWidth="3" />
                      {p.live && (
                        <circle
                          className="scoin__rarc"
                          cx="29"
                          cy="29"
                          r={R}
                          strokeWidth="3"
                          strokeDasharray={C}
                          strokeDashoffset={C * (1 - frac)}
                        />
                      )}
                    </>
                  )}
                </svg>
                <span className="scoin__icon">
                  {p.image ? (
                    <img src={p.image} alt="" loading="lazy" draggable={false} />
                  ) : (
                    (p.symbol || '?').slice(0, 3)
                  )}
                </span>
              </span>
              <span className="scoin__sym">{p.symbol}</span>
              <span className="scoin__meta">
                {isHot
                  ? fmtPool(p.totalPool, p.decimals)
                  : p.live
                    ? `${Math.floor(Math.max(0, tl) / 60)}:${String(Math.max(0, tl) % 60).padStart(2, '0')}`
                    : '\u00a0'}
              </span>
            </button>
          );
        })}
      </div>

      {/* instant games */}
      <div className="lobby__sec">
        <span>
          <Zap size={11} aria-hidden="true" /> {t('v2.lobby.games')}
        </span>
        <span className="lobby__sec-note">{t('v2.lobby.maxwin')}</span>
      </div>
      <div className="lobby__games">
        {GAMES.map((g, i) => {
          const inner = (
            <>
              {g.hot && <span className="gcard__hot">HOT</span>}
              <g.Art />
              <span className="gcard__name">{g.name}</span>
              <span className="gcard__mult">{g.mult}</span>
            </>
          );
          return g.url ? (
            <motion.a
              key={g.id}
              className="gcard"
              href={g.url}
              target="_blank"
              rel="noreferrer"
              custom={i}
              variants={cardStagger}
              initial="hidden"
              animate="show"
            >
              {inner}
            </motion.a>
          ) : (
            <motion.button
              key={g.id}
              type="button"
              className="gcard"
              onClick={
                g.id === 'wheel'
                  ? onGoWheel
                  : g.id === 'plinko'
                    ? onGoPlinko
                    : g.id === 'rocket'
                      ? onGoRocket
                      : onGoCoinFlip
              }
              custom={i}
              variants={cardStagger}
              initial="hidden"
              animate="show"
            >
              {inner}
            </motion.button>
          );
        })}
        <motion.div
          className="gcard gcard--soon"
          custom={GAMES.length}
          variants={cardStagger}
          initial="hidden"
          animate="show"
          aria-disabled="true"
        >
          <DiceArt />
          <span className="gcard__name">{t('v2.lobby.soon')}</span>
          <span className="gcard__mult">{t('v2.lobby.soonSub')}</span>
        </motion.div>
      </div>

    </section>
  );
}
